import { z } from 'zod';
import { idSchema, nameSchema, objectIdSchema } from '../../validation';
import { orderContactSchema, orderBeneficiaryDestinationSchema } from './order.beneficiary.model';
import { orderDestinationStatusSchema } from './order.status.model';
import { addressSchema } from '../../common';
import { OrderEventTypes } from './order.timeline.model';
import { RegionSchema } from '../region';

/**
 * A contact on a stop. Unlike the shipper / sender / receiver contact, the
 * phone is optional: a user often names the party at a stop ("شركة
 * العيسائي") without giving its number, and the order must not store
 * someone else's number as theirs. Filing paths that need a phone (Bayan)
 * fall back to the shipper at build time.
 */
export const orderDestinationContactSchema = orderContactSchema.partial({ phone: true });
export type OrderDestinationContact = z.infer<typeof orderDestinationContactSchema>;

/** if zero, then it means it did not happen */
export const orderTimeSchema = z.coerce.number().min(0).default(0);

export const orderDestinationTimeSchema = z.object({
  /** the time in minutes to it took from start or heading to the destination until the end or loading / unloading of items */
  totalJourneyTime: orderTimeSchema,
  /** the time in minutes it took to wait at the destination */
  totalWaitingTime: orderTimeSchema,
  /** the time in minutes it took to travel from the driver location to the destination */
  totalInTransitTime: orderTimeSchema,
  /** the total time in minutes for which the truck was stopped because of legal Prevention at while heading to this destination */
  totalLegalPreventionTime: orderTimeSchema,

  /** the total time in minutes for the driver to rest */
  totalRestTime: orderTimeSchema,

  /** the total delay time in minutes for arriving at the destination.
   *
   * This will be zero if we reached the destination within the min/max date.
   *
   * This will be calculated as soon as we arrive to the destination NOT when we drop off the items.
   *
   * because it's the customer responsibility to load/unload items and we do not have control over it.
   */
  totalDelayOnArrivalTime: orderTimeSchema,
});
/** Destination time time matrix in minutes such as waiting, in transit time, etc*/
export type OrderDestinationTime = z.infer<typeof orderDestinationTimeSchema>;
export type TimeMatrixConfig<T> = Readonly<{
  [key in keyof T]:
    | undefined
    | {
        /** **ONLY ONE** of the array element should apply for the calculation */
        from: OrderEventTypes;
        /** **ONLY ONE** of the array element should apply for the calculation */
        to: OrderEventTypes;
        subtract: (keyof T)[] | undefined;
      };
}>;

export const orderDestinationTypeSchema = z.enum(['pickup', 'dropoff']);
export type OrderDestinationType = z.infer<typeof orderDestinationTypeSchema>;

export const orderSavedPlaceSchema = z.object({
  /** this id will be coming from Saved places collection */
  _id: objectIdSchema,

  /** the custom name of the place added by the user */
  name: nameSchema,
});
export type OrderSavedPlace = z.infer<typeof orderSavedPlaceSchema>;
export const orderDestinationRegionSchema = RegionSchema.model.pick({
  _id: true,
  customCode: true,
  displayName: true,
  type: true,
});
export type OrderDestinationRegion = z.infer<typeof orderDestinationRegionSchema>;

export const orderDestinationSchema = z.object({
  /**
   *  This is NOT object id, it's just a unique random id (12321) that identifies the destination.
   *  This is used to update status or anything realted to the destination.
   */
  id: idSchema,
  /** this will be coming from `Region` collection */
  region: orderDestinationRegionSchema.optional(),
  /** destination status  */
  status: orderDestinationStatusSchema,
  type: orderDestinationTypeSchema, // pickup or dropOff
  address: addressSchema,

  /** is the location accurate (exact) or approximate.
   * Sometimes the location is approximate because the user may not have the exact location yet.
   * Later on the location could be changed to accurate if the user provides the exact location.
   */
  isAccurateLocation: z.coerce.boolean(),

  /**
   * Optional Google Maps share URL the user supplied for this stop, kept
   * verbatim as provenance for `address` / `isAccurateLocation`.
   *
   * The pin is always resolved from the link at the time it's pasted, so
   * this is never the source of truth for *where* the stop is — it's what
   * lets someone re-open the original link, and what travels onto
   * `SavedLocation.locationUrl` when the stop is later saved as a place.
   */
  locationUrl: z.string().url().optional(),

  /**
   * The address as the person described it — «حي الربوة، بجانب مسجد
   * الراجحي». `address.fullAddress` is what Google calls the pin, which
   * can be a park or a street they never said; the وثيقة prints this
   * text when it is set (`printedStopAddress`).
   */
  addressDetail: z.string().trim().max(300).optional(),

  /** saved place is a place where our business user can reuse the same place to pickup or dropoff from - we can get the (contact & customer) when the user selects a saved location */
  savedPlace: orderSavedPlaceSchema.optional(),
  /**
   * This is a third party customer which means a customer of our Business user.
   *
   * If empty then it means that our business user did assign the destination to specific customer
   * */
  beneficiary: orderBeneficiaryDestinationSchema.optional(),

  /**
   * the list of contacts for this destination.
   * this would be empty if the place we're going to is NOT able to get contact for one person such as a restaurant, mall, etc.
   */
  contacts: z.array(orderDestinationContactSchema).optional(),
  /** any instructions / notes  for the destination */
  instructions: z.string().optional(),
  /** time matrix in minutes */
  time: orderDestinationTimeSchema,
});
export type OrderDestination = z.infer<typeof orderDestinationSchema>;
